import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Play, RotateCcw } from "lucide-react";
import { playJingle, speak } from "../lib/speech";
import Header from "../components/Header";
import UserNav from "../components/UserNav";

const PRESETS = [1, 2, 5, 10, 15, 20, 30];

export default function TimerPage() {
  const navigate = useNavigate();
  const [total, setTotal] = useState(null);
  const [left, setLeft] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (left === null || left <= 0) return;
    intervalRef.current = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(intervalRef.current);
  }, [left]);

  useEffect(() => {
    if (left === 0) {
      playJingle("fun");
      speak("Time is up. All done!");
    }
  }, [left === 0]); // eslint-disable-line

  const start = (mins) => {
    setTotal(mins * 60);
    setLeft(mins * 60);
    speak(`${mins} minute${mins > 1 ? "s" : ""}. Go!`);
  };

  const reset = () => { setTotal(null); setLeft(null); };

  const pct = total && left !== null ? left / total : 1;
  const running = left !== null && left > 0;
  const finished = left === 0;
  const color = pct > 0.5 ? "#48BB78" : pct > 0.25 ? "#ECC94B" : "#ED8936";

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div data-testid="timer-page" className="min-h-screen bg-background">
      <Header />
      <main className="max-w-2xl mx-auto px-4 py-8 pb-32 text-center">
        <div className="text-left">
          <button data-testid="back-button" onClick={() => navigate("/use")} className="min-h-[48px] px-4 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
            <ArrowLeft size={20} strokeWidth={2.5} /> Back
          </button>
        </div>

        <h1 className="mt-6 text-3xl font-extrabold tracking-tight">How long?</h1>

        {!total && (
          <div data-testid="timer-presets" className="mt-8 grid grid-cols-3 sm:grid-cols-4 gap-3">
            {PRESETS.map((m) => (
              <button
                key={m}
                data-testid={`timer-preset-${m}`}
                onClick={() => start(m)}
                className="min-h-[96px] rounded-3xl border-2 border-foreground/15 bg-white text-2xl font-extrabold transition-transform duration-200 ease-out hover:-translate-y-0.5 active:scale-95"
              >
                {m}<span className="block text-sm font-bold text-muted-foreground">min</span>
              </button>
            ))}
          </div>
        )}

        {total && (
          <div className="mt-8">
            <div
              data-testid="timer-circle"
              className="mx-auto w-64 h-64 rounded-full grid place-items-center transition-[background] duration-500"
              style={{ background: `conic-gradient(${color} ${pct * 360}deg, #E2E8F0 0deg)` }}
            >
              <div className="w-52 h-52 rounded-full bg-white grid place-items-center">
                {finished ? (
                  <span className="text-3xl font-extrabold text-[#2F855A]">All done!</span>
                ) : (
                  <span data-testid="timer-display" className="text-6xl font-extrabold tabular-nums">{fmt(left)}</span>
                )}
              </div>
            </div>
            <div className="mt-8 flex justify-center gap-3">
              {running && (
                <button data-testid="timer-finish-early" onClick={() => setLeft(0)} className="min-h-[64px] px-8 rounded-full bg-[#48BB78] text-white text-xl font-bold flex items-center gap-2">
                  <Play size={24} strokeWidth={3} /> Done early
                </button>
              )}
              <button data-testid="timer-reset" onClick={reset} className="min-h-[64px] px-8 rounded-full border-2 border-foreground/25 bg-white text-xl font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
                <RotateCcw size={24} strokeWidth={2.5} /> {finished ? "Again" : "Cancel"}
              </button>
            </div>
          </div>
        )}
      </main>
      <UserNav />
    </div>
  );
}
